'use client';

import React, { useEffect, useRef } from 'react';

import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Tooltip from '@mui/material/Tooltip';

import { Typography } from '@mui/material';

import type { NotificationTypeEnum } from '@/types/enums/notification-type-enum';
import { t } from '@/utils/lang';
import { notificationManager } from '@/utils/notification-navigator';

export interface NotificationItem {
    id: string;
    title: string;
    description: string;
    createdAt: string;
    status: boolean;
    isRead: boolean;
    onClick?: () => void;
}

interface Props {
    items: NotificationItem[];
    onEndReached?: () => void;
    isFetchingNext?: boolean;
    hasNextPage?: boolean;
}

const MenuListNotification: React.FC<Props> = ({
    items,
    onEndReached,
    isFetchingNext,
    hasNextPage,
}) => {
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const listRef = useRef<HTMLUListElement | null>(null);

    useEffect(() => {
        if (!listRef.current || !sentinelRef.current || !onEndReached) return;

        const io = new IntersectionObserver(
            entries => {
                const hit = entries[0]?.isIntersecting;

                if (hit && !isFetchingNext && !!hasNextPage) onEndReached();
            },
            { root: listRef.current, rootMargin: '48px', threshold: 0.1 }
        );

        io.observe(sentinelRef.current);

        return () => io.disconnect();
    }, [items.length, isFetchingNext, hasNextPage, onEndReached]);

    if (items.length === 0) {
        return (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                <NotificationsOffIcon fontSize='large' />
                <Typography variant='body2' sx={{ mt: 1 }}>
                    Chưa có thông báo
                </Typography>
            </Box>
        );
    }

    return (
        <MenuList
            ref={listRef}
            sx={{ p: 0, maxHeight: 360, overflowY: 'auto' }}
            aria-busy={isFetchingNext}
        >
            {items.map(item => {
                return (
                    <MenuItem
                        key={item.id}
                        onClick={item.onClick}
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            py: 1.5,
                            px: 2,
                            borderBottom: item.isRead
                                ? '1px solid #F3F3F3'
                                : '1px solid rgba(16,66,142,0.1)',
                            backgroundColor: item.isRead
                                ? 'transparent'
                                : '#EBF1FA80',
                        }}
                    >
                        <Box sx={{ flex: 1, p: 2 }}>
                            <Box
                                component='span'
                                sx={{
                                    display: 'block',
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: '#3A3541DE',
                                }}
                            >
                                {t(
                                    notificationManager.getTitle(
                                        item.title as NotificationTypeEnum
                                    )
                                )}
                            </Box>
                            <Tooltip title={item.description} arrow>
                                <Typography
                                    component='span'
                                    variant='caption'
                                    sx={{
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: 320,
                                        mt: 0.5,
                                        cursor: 'pointer',
                                    }}
                                >
                                    {item.description}
                                </Typography>
                            </Tooltip>
                            <Box
                                component='span'
                                sx={{
                                    display: 'block',
                                    fontSize: 12,
                                    fontWeight: 400,
                                    color: item.isRead
                                        ? '#3A3541AD'
                                        : '#10428E',
                                    mt: 0.5,
                                }}
                            >
                                {item.status}
                            </Box>
                        </Box>
                        <Box
                            component='span'
                            sx={{
                                fontSize: 12,
                                fontWeight: 400,
                                color: '#3A354161',
                                ml: 1,
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                            }}
                        >
                            {item.createdAt}
                        </Box>
                    </MenuItem>
                );
            })}
            <div ref={sentinelRef} style={{ height: 1 }} />
            {isFetchingNext && (
                <Box sx={{ p: 2, textAlign: 'center' }}>Đang tải thêm...</Box>
            )}
        </MenuList>
    );
};

export default MenuListNotification;
